import { useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/misc/Breadcrumbs'
import { StatCard } from '@/components/misc/StatCard'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Search, Plus, MoreHorizontal, CreditCard, DollarSign } from 'lucide-react'
import { getOrders, getOrderStats } from '@/services/mock/orders'

export default function TransactionsPage() {
  const [stats, setStats] = useState(null)
  const [transactions, setTransactions] = useState([])
  const [page, setPage] = useState(1)
  const [size] = useState(10)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [loading, setLoading] = useState(false)

  const fetchStats = async () => {
    try {
      const s = await getOrderStats()
      setStats(s)
    } catch (e) {
      console.error(e)
    }
  }

  const fetchTransactions = async () => {
    setLoading(true)
    try {
      const res = await getOrders({ page, size, search, status: statusFilter })
      setTransactions(res.data)
      setTotal(res.meta.total)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()
    fetchTransactions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, statusFilter])

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Transaction</h1>
          <Breadcrumbs />
        </div>
      </div>

      {/* Top stats and payment method */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 lg:col-span-2">
          <StatCard title="Total Revenue" value={stats ? stats.totalSales : 15045} change={14.4} changeType="percentage" format="currency" />
          <StatCard title="Completed Transactions" value={3150} change={20} changeType="percentage" />
          <StatCard title="Pending Transactions" value={150} change={85} changeType="number" />
          <StatCard title="Failed Transactions" value={75} change={-15} changeType="number" />
        </div>

        <div className="bg-card border rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium">Payment Method</h3>
            </div>
            <div className="text-sm text-muted-foreground">Status: Active</div>
          </div>
          <div className="mt-4 flex items-center gap-4">
            <div className="flex-1">
              <div className="bg-gradient-to-r from-sky-400 to-emerald-400 rounded-xl p-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="text-sm">Finaci</div>
                  <div className="text-sm">**** 2345</div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-xs">Nomam Manzoor</div>
                  <div className="text-xs">02/30</div>
                </div>
              </div>
              <div className="mt-3 text-sm text-muted-foreground">
                <div>Transactions: 1,250</div>
                <div>Revenue: $50,000</div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <Button variant="ghost" size="sm" className="flex-1">Add Card</Button>
            <Button variant="destructive" size="sm">Deactivate</Button>
          </div>
        </div>
      </div>

      {/* Tabs, search and table */}
      <section className="space-y-4">
        <div className="bg-card border p-4 rounded-xl">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => setStatusFilter('')} className="px-3 py-1 rounded-full bg-primary text-primary-foreground text-sm">All order (240)</button>
              <button onClick={() => setStatusFilter('COMPLETED')} className="px-3 py-1 rounded-full text-sm bg-muted/50">Completed</button>
              <button onClick={() => setStatusFilter('PENDING')} className="px-3 py-1 rounded-full text-sm bg-muted/50">Pending</button>
              <button onClick={() => setStatusFilter('CANCELED')} className="px-3 py-1 rounded-full text-sm bg-muted/50">Canceled</button>
            </div>

            <div className="flex items-center gap-2 w-full md:w-auto">
              <div className="flex-1 md:flex-none">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} placeholder="Search payment history" className="pl-10" />
                </div>
              </div>

              <Button variant="ghost" size="sm">Filter</Button>
              <Button variant="ghost" size="sm">Export</Button>
            </div>
          </div>
        </div>

        <div className="bg-card border p-4 rounded-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="p-3">Customer Id</th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Date</th>
                  <th className="p-3">Total</th>
                  <th className="p-3">Method</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="p-4 text-center text-muted-foreground">Loading...</td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-4 text-center text-muted-foreground">No transactions found</td>
                  </tr>
                ) : (
                  transactions.map((t, idx) => (
                    <tr key={t.id} className="border-b last:border-b-0 hover:bg-muted/50">
                      <td className="p-3 align-middle">{t.orderNumber}</td>
                      <td className="p-3 align-middle">{t.customerName}</td>
                      <td className="p-3 align-middle">{new Date(t.createdAt).toLocaleDateString()}</td>
                      <td className="p-3 align-middle">${t.total.toFixed(2)}</td>
                      <td className="p-3 align-middle">{t.paymentMethod}</td>
                      <td className="p-3 align-middle">{t.paymentStatus}</td>
                      <td className="p-3 align-middle"><a className="text-primary">View Details</a></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">Showing {(page - 1) * size + 1} - {Math.min(page * size, total)} of {total}</div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</Button>
              <div className="px-3 py-1 rounded bg-muted text-sm">{page}</div>
              <Button variant="ghost" size="sm" disabled={page * size >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}